/**
 * Document template engine (Phase 8i.1) — PURE, mirrored in
 * frontend/src/lib/templateEngine.ts. Keep the two byte-identical so server
 * generation and (later) the 8i.3 editor preview can never drift.
 *
 * Three double-brace element types:
 *   MERGE FIELDS   {{customer_name}}        -> resolved text (unknown → literal)
 *   DYNAMIC BLOCKS {{estimate_line_items_table}} -> generated HTML
 *   INPUT FIELDS   {{signature}} {{choicebox:A|B|C}} -> placeholders (8i.2 makes
 *                  them interactive) with a stable, unique blockId per document.
 *
 * The engine is pure: all live data (the estimate calc, deposit total, rep and
 * contractor names, generation date) is precomputed by the caller and passed in
 * `ctx`, so this module never does I/O and reuses the shared estimate engine's
 * result (ctx.calc = calcEstimate(...)) rather than recomputing.
 */

import { formatJobNo } from './jobNumber';
import { billableWeight } from './estimate';
import { Leg, resolvedParts, streetLine } from './addressParts';
import {
  DvpQuote, DVP_NAME, dvpMoney,
  dvpAcceptOption, dvpDeclineOption, ENHANCED_LD_RATE_LABEL,
  dvpSectionTwoAHtml, dvpOptionsHtml,
} from './dvp';

export interface TemplateContext {
  calc?: any; // result of calcEstimate(lead, ...) — { items, subtotal, tax, taxRate, total, totalDiscountAmt, discountedSubtotal }
  depositTotal?: number; // Σ deposit-type payment amounts on the lead
  repName?: string;
  contractorName?: string;
  generatedAt?: string; // ISO; defaults to now
  ccFeeRate?: number;      // company.cc_fee_rate — D1/A11, never a literal in content
  documentNumber?: string; // the generated document's number, for the identity line
  /**
   * DVP — the declared-value quote for this lead, or null/undefined when the
   * option does not apply (every local move, and any caller that has not built
   * one). The two DVP blocks render to NOTHING when it is absent, so a local
   * contract carrying the placeholders is still a correct local contract.
   */
  dvp?: DvpQuote | null;
}

const esc = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const money = (v: unknown): string => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '$0.00';
  return '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Long distance (lib/moveType.isLongDistance, inlined so this file stays self-contained). */
const isLdLead = (lead: any): boolean => String(lead?.move_type ?? '').toLowerCase().includes('long distance');

const fmtDate = (v: unknown): string => {
  if (!v) return '';
  const s = String(v);
  // Format date-only 'YYYY-MM-DD' as its exact calendar date (no timezone shift).
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// ---- Reference (consumed by the 8i.3 editor) -------------------------------

export const TEMPLATE_FIELD_REFERENCE = {
  // Grouped merge fields (category drives the editor's collapsible groups).
  mergeFields: [
    { field: 'customer_name', category: 'Customer', desc: 'Customer first + last name' },
    { field: 'customer_phone', category: 'Customer', desc: 'Customer phone' },
    { field: 'customer_email', category: 'Customer', desc: 'Customer email' },
    { field: 'job_number', category: 'Job', desc: 'Job number (e.g. #4042)' },
    { field: 'move_date', category: 'Job', desc: 'Move date (blank on long distance — LD clients get windows, not a date)' },
    { field: 'confirmed_move_date', category: 'Job', desc: 'Confirmed move date — always shown, LD included (Confirmation Form)' },
    { field: 'estimate_date', category: 'Job', desc: 'Generation date' },
    { field: 'move_size', category: 'Job', desc: 'Move size' },
    { field: 'move_type', category: 'Job', desc: 'Move type' },
    { field: 'weight', category: 'Job', desc: 'Estimated weight (lbs)' },
    { field: 'document_number', category: 'Job', desc: 'This document\'s number, e.g. #1000-2' },
    { field: 'minimum_weight', category: 'Job', desc: "The lead's own minimum billable weight (lbs)" },
    { field: 'cc_fee_rate', category: 'Job', desc: 'Card processing fee rate, e.g. 3% (from company settings)' },
    { field: 'cuft', category: 'Job', desc: 'Estimated volume (CuFT)' },
    { field: 'origin_address', category: 'Addresses & timing', desc: 'Origin address' },
    { field: 'origin_street', category: 'Addresses & timing', desc: 'Origin street line, with unit' },
    { field: 'origin_city', category: 'Addresses & timing', desc: 'Origin city' },
    { field: 'origin_province', category: 'Addresses & timing', desc: 'Origin province (e.g. ON)' },
    { field: 'origin_postal_code', category: 'Addresses & timing', desc: 'Origin postal code' },
    { field: 'destination_address', category: 'Addresses & timing', desc: 'Destination address' },
    { field: 'destination_street', category: 'Addresses & timing', desc: 'Destination street line, with unit' },
    { field: 'destination_city', category: 'Addresses & timing', desc: 'Destination city' },
    { field: 'destination_province', category: 'Addresses & timing', desc: 'Destination province (e.g. NS)' },
    { field: 'destination_postal_code', category: 'Addresses & timing', desc: 'Destination postal code' },
    { field: 'pickup_date_range', category: 'Addresses & timing', desc: 'Pickup date range (LD) or move date' },
    { field: 'dropoff_date_range', category: 'Addresses & timing', desc: 'Drop-off date range (LD)' },
    { field: 'pickup_time_window', category: 'Addresses & timing', desc: 'Pickup arrival window' },
    { field: 'dropoff_time_window', category: 'Addresses & timing', desc: 'Drop-off arrival window' },
    { field: 'estimate_total', category: 'Financial', desc: 'Estimate grand total' },
    { field: 'deposit_amount', category: 'Financial', desc: 'Deposit paid to date' },
    { field: 'declared_value', category: 'Coverage', desc: 'Declared Value Protection limit (long distance only)' },
    { field: 'dvp_premium', category: 'Coverage', desc: 'Declared Value Protection premium for the declared value' },
    { field: 'dvp_deductible', category: 'Coverage', desc: 'Declared Value Protection deductible, per claim' },
    { field: 'rep_name', category: 'Team', desc: 'Assigned sales rep' },
    { field: 'contractor_name', category: 'Team', desc: 'Assigned contractor' },
  ],
  dynamicBlocks: [
    { field: 'estimate_line_items_table', desc: 'Full estimate line-items table with totals' },
    { field: 'dvp_election', desc: 'Declared Value Protection: both options with amounts, the election box and its initials (long distance only)' },
    { field: 'dvp_section_2a', desc: 'Declared Value Protection: Section 2A liability terms (long distance only; dropped if the client declines)' },
  ],
  inputs: [
    { field: 'signature', insert: '{{signature}}', desc: 'Signature capture block' },
    { field: 'choicebox', insert: '{{choicebox:Option 1|Option 2}}', desc: 'Multiple-choice selection (edit the options)' },
    { field: 'initials', insert: '{{initials}}', desc: 'Initials block — a signature event with the same audit trail' },
  ],
  // Ordered category list for the editor.
  categories: ['Customer', 'Job', 'Addresses & timing', 'Financial', 'Coverage', 'Team'],
};

// ---- Sample data for the editor preview + field tooltips --------------------
// A fabricated lead + context exercising every field (never a real job). Shared
// so tooltip examples and the preview render from one source.
export const SAMPLE_LEAD = {
  id: 'sample-lead',
  job_no: 4042,
  first_name: 'Sarah',
  last_name: 'Kowalski',
  phone: '(416) 555-0148',
  email: 'sarah.kowalski@example.com',
  move_date: '2026-08-14',
  move_type: 'Long Distance Relocation',
  minimum_weight: 2000,
  move_size: '3 Bedroom Home',
  weight_lbs: 12065,
  cubic_feet: 1723,
  origin_address: 'Unit 5, 42 Maple Ave, Toronto, ON, M4Y 1H1',
  origin_unit: '5',
  origin_street: '42 Maple Ave',
  origin_city: 'Toronto',
  origin_province: 'ON',
  origin_postal_code: 'M4Y 1H1',
  origin_country: 'CA',
  destination_address: '8 Harbour St, Halifax, NS, B3J 1H6',
  destination_street: '8 Harbour St',
  destination_city: 'Halifax',
  destination_province: 'NS',
  destination_postal_code: 'B3J 1H6',
  destination_country: 'CA',
  pickup_start: '2026-08-14',
  pickup_end: '2026-08-16',
  dropoff_start: '2026-08-20',
  dropoff_end: '2026-08-22',
  pickup_arrival_window: '9am - 11am',
  dropoff_arrival_window: '1pm - 4pm',
};

export const SAMPLE_CONTEXT: TemplateContext = {
  calc: {
    items: [
      { desc: 'Transportation / Weight Charge', quantity: 12065, rate: 0.79, rateKind: 'money', unitLabel: 'lb', source: 'base', net: 9531.35, discountAmt: 0 },
      { desc: 'Fuel Surcharge (15% of base)', quantity: 1, rate: 15, rateKind: 'pct', source: 'fee', net: 1429.7, discountAmt: 0 },
      { desc: 'Stair Carry', quantity: 2, rate: 75, rateKind: 'unit', unitLabel: 'flight', source: 'fee', net: 150, discountAmt: 0 },
    ],
    subtotal: 11111.05,
    totalDiscountAmt: 0,
    discountedSubtotal: 11111.05,
    tax: 1444.44,
    taxRate: 13,
    total: 12555.49,
  },
  depositTotal: 500,
  repName: 'Daryl Wells',
  contractorName: 'Trillium Movers',
  generatedAt: '2026-08-02T12:00:00Z',
  // The sample lead is long distance, so the editor preview exercises the DVP
  // blocks rather than rendering them to nothing. Figures are the real formula
  // on the sample's own weight: 12,065 lb x $6.00 = $72,390 declared, at $18.00
  // per $1,000 pro-rata. Well clear of the $450 floor, so the preview shows the
  // ordinary case; the floor binds under a $25,000 declared value.
  dvp: {
    offered: true,
    weight: 12065,
    minimum: 72390,
    declaredValue: 72390,
    premium: 1303.02,
    calculatedPremium: 1303.02,
    overridden: false,
    minPremium: 450,
    premiumAtMinimum: false,
    deductible: 500,
    elected: false,
    unset: false,
  },
};

/**
 * Validate template content (shared front + back):
 *   errors        — blocking problems (malformed choiceboxes).
 *   unknownFields — non-blocking: unrecognized {{merge_fields}} that will render
 *                   literally (broker may intend a future field).
 */
export function validateTemplateContent(content: string): { errors: string[]; unknownFields: string[] } {
  const errors: string[] = [];
  const src = String(content ?? '');

  // Choicebox syntax: needs at least two non-empty options separated by '|'.
  const cbMatches = src.match(/\{\{\s*choicebox\s*:([^}]*)\}\}/gi) ?? [];
  for (const m of cbMatches) {
    const inner = m.replace(/\{\{\s*choicebox\s*:/i, '').replace(/\}\}\s*$/, '');
    const opts = inner.split('|').map((o) => o.trim()).filter(Boolean);
    if (opts.length < 2) errors.push(`Malformed choicebox "${m}" — needs at least two options separated by "|".`);
  }

  // Unknown merge fields (not a known field, dynamic block, or 'signature').
  const known = new Set<string>([
    ...TEMPLATE_FIELD_REFERENCE.mergeFields.map((f) => f.field),
    ...TEMPLATE_FIELD_REFERENCE.dynamicBlocks.map((b) => b.field),
    'signature',
    'initials',
  ]);
  const unknownFields: string[] = [];
  const seen = new Set<string>();
  const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  let mm: RegExpExecArray | null;
  while ((mm = re.exec(src)) !== null) {
    const f = mm[1];
    if (known.has(f)) continue;
    if (!seen.has(f)) {
      seen.add(f);
      unknownFields.push(f);
    }
  }
  return { errors, unknownFields };
}

const DYNAMIC_BLOCKS = new Set(['estimate_line_items_table', 'dvp_election', 'dvp_section_2a']);

// ---- Merge fields ----------------------------------------------------------

/**
 * Resolve one merge field to escaped text. UNKNOWN fields return the literal
 * `{{field}}` (so typos are visible, never silently blank) and warn in dev.
 */
export function renderMergeField(field: string, lead: any, ctx: TemplateContext = {}): string {
  const s = (v: unknown) => esc(v ?? '');
  switch (field) {
    case 'job_number':
      return esc(formatJobNo(lead));
    case 'document_number':
      // Supplied by the generator; falls back to the job number so the identity
      // line is never blank in the editor preview.
      return esc(ctx.documentNumber ?? formatJobNo(lead));
    case 'minimum_weight':
      // D1/MW: the lead's OWN figure, replacing the hardcoded 1500/2000
      // contradiction. Falls back to the system default so a contract never
      // renders a blank where a number belongs.
      return esc(Number(lead.minimum_weight ?? 2000).toLocaleString());
    case 'cc_fee_rate':
      // D1/A11: never a literal "3%". If the rate changes in Settings, every
      // future contract follows; a hardcoded figure would silently go stale.
      return esc(`${Number(ctx.ccFeeRate ?? 3)}%`);
    case 'customer_name':
      return esc(`${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim());
    case 'customer_phone':
      return s(lead.phone);
    case 'customer_email':
      return s(lead.email);
    case 'confirmed_move_date':
      // The confirmed date, on every move type. For the Confirmation Form, which
      // is how a long-distance client is told the date once the rep sets it;
      // {{move_date}} stays blank for LD everywhere else.
      return esc(fmtDate(lead.move_date));
    case 'move_date':
      // Never a move date to a long-distance client: LD runs on pickup / delivery
      // windows ({{pickup_date_range}}, {{pickup_time_window}}, …). Empty, not '—'.
      return isLdLead(lead) ? '' : esc(fmtDate(lead.move_date));
    case 'estimate_date':
      return esc(fmtDate(ctx.generatedAt ?? new Date().toISOString()));
    case 'pickup_time_window':
      return s(lead.pickup_arrival_window);
    case 'dropoff_time_window':
      return s(lead.dropoff_arrival_window);
    case 'pickup_date_range':
      return lead.pickup_start && lead.pickup_end
        ? esc(`${fmtDate(lead.pickup_start)} – ${fmtDate(lead.pickup_end)}`)
        : isLdLead(lead) ? '' : esc(fmtDate(lead.move_date));
    case 'dropoff_date_range':
      return lead.dropoff_start && lead.dropoff_end ? esc(`${fmtDate(lead.dropoff_start)} – ${fmtDate(lead.dropoff_end)}`) : '';
    case 'origin_address':
      return s(lead.origin_address);
    case 'destination_address':
      return s(lead.destination_address);
    case 'origin_street':
    case 'origin_city':
    case 'origin_province':
    case 'origin_postal_code':
    case 'destination_street':
    case 'destination_city':
    case 'destination_province':
    case 'destination_postal_code': {
      // Migration 052: from the lead's address columns, or split from the line
      // on an old lead. Empty when neither gives an answer.
      const [leg, ...rest] = field.split('_');
      const parts = resolvedParts(lead, leg as Leg);
      if (!parts) return '';
      const key = rest.join('_') as 'street' | 'city' | 'province' | 'postal_code';
      return s(key === 'street' ? streetLine(parts) : parts[key]);
    }
    case 'move_size':
      return s(lead.move_size);
    case 'move_type':
      return s(lead.move_type);
    case 'weight':
      // THE BILLED WEIGHT, AND NEVER A BLANK.
      //
      // This used to read weight_lbs directly and return '' when it was 0 —
      // which is the DEFAULT for a new lead (migration 001) and is never
      // corrected unless a human edits the field. A contract could therefore go
      // out reading "Estimated weight " with nothing after it, and nothing
      // errored.
      //
      // It now renders billableWeight — max(weight_lbs, minimum_weight), the
      // figure estimate.ts calls canonical ("displayed = stored = billed") and
      // the one the transportation charge is actually computed on. That closes
      // the blank (minimum_weight is NOT NULL DEFAULT 2000) and, just as
      // importantly, makes the weight on the page agree with the weight the
      // Declared Value Protection minimum is derived from. Two different weights
      // on one contract would not reconcile.
      //
      // The em-dash is the last resort for a company running a zero minimum: a
      // stated "unknown" beats a silent gap.
      return num(billableWeight(lead)) ? esc(`${num(billableWeight(lead)).toLocaleString()} lbs`) : '—';
    case 'cuft':
      return num(lead.cubic_feet) ? esc(`${num(lead.cubic_feet).toLocaleString()} CuFT`) : '';
    case 'estimate_total':
      return ctx.calc ? money(ctx.calc.total) : '';
    case 'deposit_amount':
      return num(ctx.depositTotal) ? money(ctx.depositTotal) : '';
    // ---- Declared Value Protection -----------------------------------------
    // All three render an em-dash rather than a blank when DVP does not apply
    // (every local move). A money field that silently disappears is the same
    // defect {{weight}} carried above.
    case 'declared_value':
      return ctx.dvp?.offered ? esc(dvpMoney(ctx.dvp.declaredValue)) : '—';
    case 'dvp_premium':
      return ctx.dvp?.offered ? esc(dvpMoney(ctx.dvp.premium)) : '—';
    case 'dvp_deductible':
      return ctx.dvp?.offered ? esc(dvpMoney(ctx.dvp.deductible)) : '—';
    case 'rep_name':
      return s(ctx.repName);
    case 'contractor_name':
      return s(ctx.contractorName);
    default:
      // Warn in dev (guarded so it is safe in both Node and the browser).
      if (typeof console !== 'undefined' && !(typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production')) {
        // eslint-disable-next-line no-console
        console.warn(`[templateEngine] unknown merge field: ${field}`);
      }
      return `{{${field}}}`; // leave visible
  }
}

// ---- Dynamic blocks --------------------------------------------------------

/**
 * DVP — THE ELECTION: both options with their amounts, the choice box, and the
 * initials that ratify it.
 *
 * A SIGNATURE RATIFIES IT, NOT INITIALS. Initials are legally sufficient — a
 * mark made with intent to sign is a signature, whatever its shape — but this
 * election decides the carrier's liability ceiling for the whole shipment, and a
 * two-letter box beside it understates that. The block asks for the same
 * signature, printed name and date the Shipper Authorization does.
 *
 * ONE BLOCK EMITS BOTH, deliberately. The signature cannot be a separate
 * {{signature}} in the template, because this block renders to NOTHING on a
 * local move — and a stray signature line on a local contract would be an orphan
 * mark asking the client to ratify an election they were never offered.
 *
 * THE BLOCK IDS ARE NAMED, NOT NUMBERED. renderTemplate counts literal
 * {{choicebox:...}} and {{signature}} occurrences into choice-1, sig-1, … in its
 * own passes; a dynamic block renders inside the second pass and cannot reach
 * those counters. 'choice-dvp' and 'sig-dvp' are unique by construction, which
 * is all parseInputBlocks and the fill need; they are self-describing in the
 * audit trail; and the shared 'dvp' suffix is what ratifiedBy() in
 * routes/documents.ts pairs them on, which is why they are named alike rather
 * than merely uniquely.
 */
function renderDvpElection(ctx: TemplateContext): string {
  const q = ctx.dvp;
  // NOT OFFERED RENDERS NOTHING AT ALL — not a placeholder, not an empty box.
  // Local moves carry Enhanced Coverage at $0.60/lb and are never sold DVP, so
  // the correct local contract is one with no trace of this section.
  if (!q || !q.offered) return '';

  const options = [dvpAcceptOption(q), dvpDeclineOption()];
  // ELECTED ON THE JOB ALREADY (061 — by the office on the Estimate tab, or by
  // the client on an earlier contract): the election is printed ticked and is
  // NOT an input block, so there is no choice-dvp to record and nothing to
  // re-decide here. The client still signs sig-dvp below to confirm it. A
  // static box rather than a pre-recorded choice row, because a choice row is
  // a mark: it would stop a regenerate superseding this version.
  const election = q.elected
    ? `<div class="choicebox">${renderChoiceOptions(options, options[0])}</div>`
    : renderChoicebox('choice-dvp', options);
  return `<h2>Valuation Coverage</h2>
${dvpOptionsHtml(q)}
${election}
${renderSignature('sig-dvp', 'Sign your coverage election', `I confirm the coverage election marked above. I understand that ${DVP_NAME} is optional, that declining it leaves Enhanced Coverage at ${ENHANCED_LD_RATE_LABEL} in place at no charge, and that ${q.overridden ? 'the premium shown is fixed for this shipment' : 'the premium shown is based on the estimated weight and is adjusted to the final scaled weight on my invoice'}.`)}`;
}

/**
 * DVP — SECTION 2A, wrapped so the FILL can drop it.
 *
 * WHY IT IS RENDERED AT ALL BEFORE THE CLIENT HAS CHOSEN. rendered_content is
 * snapshotted once, at generation (documents.ts), and never rewritten — but the
 * election happens on the document AFTER that. So "show Section 2A only when DVP
 * is elected" cannot be decided here: at generation nobody has elected anything.
 *
 * It is therefore rendered inside a marked wrapper and REMOVED AT FILL TIME when
 * the recorded election is a decline (lib/documentFill.ts). That gives both
 * halves of what the rule is for: the client reads the terms before agreeing to
 * them — they could not consent otherwise — and the finished artifact a declining
 * client signs, downloads and is emailed carries no terms for coverage they did
 * not buy.
 */
function renderDvpSectionTwoA(ctx: TemplateContext): string {
  const q = ctx.dvp;
  if (!q || !q.offered) return '';
  return `<div class="dvp-terms" data-dvp-section="2a">${dvpSectionTwoAHtml(q.declaredValue, q.deductible)}</div>`;
}

/** Estimate line-items table — mirrors the invoice table markup for consistency. */
export function renderDynamicBlock(block: string, _lead: any, ctx: TemplateContext = {}): string {
  if (block === 'dvp_election') return renderDvpElection(ctx);
  if (block === 'dvp_section_2a') return renderDvpSectionTwoA(ctx);
  if (block !== 'estimate_line_items_table') return `{{${block}}}`;
  const est = ctx.calc ?? { items: [], subtotal: 0, tax: 0, taxRate: 0, total: 0, totalDiscountAmt: 0, discountedSubtotal: 0 };

  const lineRows = (est.items ?? [])
    .map((it: any) => {
      const qty = it.rateKind === 'pct' || (it.source === 'base' && !it.unitLabel) ? '—' : num(it.quantity).toLocaleString();
      const rate =
        it.rateKind === 'pct'
          ? `${num(it.rate)}%`
          : it.rateKind === 'unit'
            ? `${money(it.rate)}/${esc(it.unitLabel ?? 'unit')}`
            : money(it.rate);
      const disc = num(it.discountAmt) > 0 ? `<div class="muted" style="font-size:11px">less discount ${money(it.discountAmt)}</div>` : '';
      return `<tr>
        <td>${esc(it.desc)}${disc}</td>
        <td class="num">${qty}</td>
        <td class="num">${rate}</td>
        <td class="num">${money(it.net)}</td>
      </tr>`;
    })
    .join('');

  const totalDiscRow =
    num(est.totalDiscountAmt) > 0
      ? `<div class="row"><span>Total discount</span><span class="num">−${money(est.totalDiscountAmt)}</span></div>
         <div class="row"><span>Discounted subtotal</span><span class="num">${money(est.discountedSubtotal)}</span></div>`
      : '';

  return `<table>
      <thead><tr><th>Description</th><th class="num">Qty</th><th class="num">Rate</th><th class="num">Amount</th></tr></thead>
      <tbody>${lineRows || '<tr><td colspan="4" class="muted">No line items.</td></tr>'}</tbody>
    </table>
    <div class="totals">
      <div class="row"><span>Subtotal</span><span class="num">${money(est.subtotal)}</span></div>
      ${totalDiscRow}
      <div class="row"><span>Tax (${num(est.taxRate)}%)</span><span class="num">${money(est.tax)}</span></div>
      <div class="row grand"><span>Total</span><span class="num">${money(est.total)}</span></div>
    </div>`;
}

// ---- Input placeholders (interactive in 8i.2) ------------------------------

function renderSignature(blockId: string, label?: string, intro?: string): string {
  // D1 — three ruled lines the CLIENT completes: signature, printed name, date.
  //
  // Nothing is pre-filled. The block used to print the signer's name beneath the
  // rule and an empty "· —" date placeholder, which reads as though the document
  // has already been signed by someone. On a contract that is worse than ugly:
  // a pre-printed name is an assertion about who signed.
  const block = `<div class="sig-wrap" data-block-id="${esc(blockId)}" data-input="signature">
      <div class="sig-fields">
        <div class="sig-line"><div class="sig-rule"></div><div class="sig-cap">Signature</div></div>
        <div class="sig-line"><div class="sig-rule"></div><div class="sig-cap">Printed name</div></div>
        <div class="sig-date"><div class="sig-rule"></div><div class="sig-cap">Date</div></div>
      </div>
    </div>`;
  // Bare for {{signature}} — the templates supply their own .sig-group wrapper
  // around it (see sig() in routes/templateContent.ts). The wrapper is built
  // here only when a DYNAMIC block asks for one, since a generated block has no
  // surrounding markup to inherit.
  if (!label) return block;
  return `<div class="sig-group"><p class="sig-label">${esc(label)}</p><p class="sig-intro">${esc(intro ?? '')}</p>${block}</div>`;
}

/**
 * D1 Item 2 — INITIALS. Legally this is a signature: UETA §2(8) asks for a mark
 * made with intent to sign, not for a particular shape of one. It gets the same
 * audit trail (028's document_signatures.kind = 'initials'), and it is a
 * separate block type only because the affordance differs — a small box beside a
 * choice, not three ruled lines at the end of a section.
 *
 * Like renderSignature, nothing is pre-filled: a pre-printed initial is an
 * assertion about who initialled.
 */
function renderInitials(blockId: string, label?: string, intro?: string): string {
  const box = `<div class="init-wrap" data-block-id="${esc(blockId)}" data-input="initials">
      <div class="init-box"></div>
      <div class="init-cap">Initials</div>
    </div>`;
  // A bare box for {{initials}} in a template (the templates supply their own
  // .sig-group wrapper around it — see initial() in routes/templateContent.ts);
  // the wrapper is built here only when a DYNAMIC block asks for one, since a
  // generated block has no surrounding markup to inherit.
  if (!label) return box;
  return `<div class="sig-group"><p class="sig-label">${esc(label)}</p><p class="sig-intro">${esc(intro ?? '')}</p>${box}</div>`;
}

/**
 * The tick, as an inline SVG rather than a font glyph.
 *
 * A checkmark CHARACTER depends on the PDF carrying a font that has one;
 * Chromium on Alpine falls back silently when it does not, and a missing tick on
 * a payment election is indistinguishable from an unselected option. A drawn
 * path always renders, at any size, in any font.
 */
const TICK =
  '<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true" focusable="false">'
  + '<path d="M3 8.5l3.2 3.3L13 4.8" fill="none" stroke="currentColor" stroke-width="2.6" '
  + 'stroke-linecap="round" stroke-linejoin="round"/></svg>';

/**
 * ONE renderer for a choicebox's options, used by BOTH the blank render and the
 * filled one (lib/documentFill.ts).
 *
 * They were briefly two: the blank form emitted ☐ glyphs while the filled copy
 * drew boxes, so the same document changed shape the moment someone chose
 * something. Two renderings of one thing is how the signature block ended up
 * with three.
 *
 * `selected` is null when nothing has been chosen — every box empty, which is
 * exactly what an unanswered question should look like.
 */
export function renderChoiceOptions(options: string[], selected: string | null): string {
  return options
    .map((o) => {
      const on = selected !== null && o === selected;
      return `<span class="opt${on ? ' chosen' : ''}">`
        + `<span class="opt-box">${on ? TICK : ''}</span>`
        + `<span class="opt-text">${esc(o)}</span>`
        + `</span>`;
    })
    .join('');
}

function renderChoicebox(blockId: string, options: string[]): string {
  return `<div class="choicebox" data-block-id="${esc(blockId)}" data-input="choicebox" data-options="${esc(options.join('|'))}">${renderChoiceOptions(options, null)}</div>`;
}

// ---- Full render -----------------------------------------------------------

/**
 * Render a template's content to an HTML fragment: merge fields, dynamic blocks,
 * and input placeholders in one pass. Choiceboxes (which contain ':' and '|')
 * are handled before the simple {{word}} pass. blockIds are numbered in document
 * order (sig-1, sig-2, choice-1, …) so 8i.2 can attach capture to them.
 */
export function renderTemplate(content: string, lead: any, ctx: TemplateContext = {}): string {
  let sigN = 0;
  let choiceN = 0;
  let initN = 0;

  // Choiceboxes first (they carry non-\w characters).
  let out = String(content ?? '').replace(/\{\{\s*choicebox\s*:\s*([^}]*)\}\}/gi, (_m, optsRaw) => {
    choiceN++;
    const options = String(optsRaw)
      .split('|')
      .map((o) => o.trim())
      .filter(Boolean);
    return renderChoicebox(`choice-${choiceN}`, options);
  });

  // Then simple {{ word }} — signature, dynamic blocks, and merge fields.
  out = out.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, field: string) => {
    if (field === 'signature') {
      sigN++;
      return renderSignature(`sig-${sigN}`);
    }
    if (field === 'initials') {
      initN++;
      return renderInitials(`init-${initN}`);
    }
    if (DYNAMIC_BLOCKS.has(field)) return renderDynamicBlock(field, lead, ctx);
    return renderMergeField(field, lead, ctx);
  });

  return out;
}

// ---- Reading the blocks back out of a rendered snapshot ---------------------

export interface InputBlock {
  blockId: string;
  kind: 'signature' | 'initials' | 'choicebox';
  /** Choiceboxes only — the exact options the client was offered. */
  options?: string[];
}

/**
 * The input blocks a GENERATED document contains, read from its snapshot.
 *
 * THE SNAPSHOT, NOT THE TEMPLATE, IS THE AUTHORITY. A template can be edited
 * after a document is generated; what the client was shown is rendered_content.
 * Deriving "which blocks must be filled" from the live template would let an
 * edit change the completion requirements of a document already in someone's
 * hands — and in the other direction, would let a removed block keep blocking a
 * document that no longer has it.
 *
 * Regex rather than a DOM parse because the backend tsconfig has no DOM lib and
 * pulling in a parser for four attributes would be the larger cost. The markup
 * is ours and is emitted by the three render* functions above, so the shapes are
 * fixed — this is not parsing arbitrary HTML.
 */
export function parseInputBlocks(html: string): InputBlock[] {
  const src = String(html ?? '');
  const out: InputBlock[] = [];
  const seen = new Set<string>();

  const re = /<div\b[^>]*\bdata-input="(signature|initials|choicebox)"[^>]*>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src)) !== null) {
    const tag = m[0];
    const kind = m[1].toLowerCase() as InputBlock['kind'];
    const blockId = /\bdata-block-id="([^"]*)"/i.exec(tag)?.[1] ?? '';
    if (!blockId || seen.has(blockId)) continue;
    seen.add(blockId);

    if (kind === 'choicebox') {
      const raw = /\bdata-options="([^"]*)"/i.exec(tag)?.[1] ?? '';
      const options = decodeEntities(raw).split('|').map((o) => o.trim()).filter(Boolean);
      out.push({ blockId, kind, options });
    } else {
      out.push({ blockId, kind });
    }
  }
  return out;
}

/**
 * A human name for a block, for the activity feed and the certificate.
 *
 * "sig-1" is a correct identifier and a useless label. A rep reading the feed
 * needs to know WHICH signature landed, and "Signature 2 of 3" answers that
 * without the template having to carry per-block titles it does not have.
 */
export function blockLabel(block: InputBlock, all: InputBlock[]): string {
  const sameKind = all.filter((b) => b.kind === block.kind);
  const n = sameKind.findIndex((b) => b.blockId === block.blockId) + 1;
  const many = sameKind.length > 1;
  if (block.kind === 'choicebox') return many ? `Selection ${n} of ${sameKind.length}` : 'Selection';
  if (block.kind === 'initials') return many ? `Initials ${n} of ${sameKind.length}` : 'Initials';
  return many ? `Signature ${n} of ${sameKind.length}` : 'Signature';
}

/**
 * data-options is attribute-escaped on the way in (esc()), so it must be
 * unescaped on the way out or an option containing '&' — "Visa & Mastercard" —
 * will never match what the client actually clicked, and the selection would be
 * rejected as not-an-option for a reason nobody could see.
 */
function decodeEntities(s: string): string {
  return String(s)
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');   // last: an entity in the source must not double-decode
}
